import { Inbox, Plus, Search } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { useMe } from "../auth/session";
import { useDebouncedValue, usePageTitle } from "../lib/hooks";
import { EmptyState, ErrorState } from "../ui/Notice";
import { PageHeader } from "../ui/PageHeader";
import { Spinner } from "../ui/Spinner";
import { useTickets } from "./api";
import { DONE_STATUSES, OPEN_STATUSES } from "./labels";
import { TicketTable } from "./TicketTable";
import "./tickets.css";

const PAGE_SIZE = 25;
type View = "open" | "done" | "all";
type Whose = "mine" | "assigned";

export function MyTicketsPage() {
  usePageTitle("Your tickets");
  const me = useMe();
  const [whose, setWhose] = useState<Whose>("mine");
  const [view, setView] = useState<View>("open");
  // Tickets assigned to you or a group you're in (DECISIONS #48): a second view, shown only
  // to people who have any.
  const assignedOpen = useTickets({ scope: "assigned", status: OPEN_STATUSES, limit: 1 });
  const showAssigned = me.groups.length > 0 || (assignedOpen.data?.total ?? 0) > 0;
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const q = useDebouncedValue(search.trim(), 250);
  const tickets = useTickets({
    scope: showAssigned && whose === "assigned" ? "assigned" : "mine",
    status: view === "open" ? OPEN_STATUSES : view === "done" ? DONE_STATUSES : undefined,
    q,
    offset,
    limit: PAGE_SIZE,
  });
  const total = tickets.data?.total ?? 0;
  const items = tickets.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Your tickets"
        sub={
          whose === "assigned" && showAssigned
            ? "Assigned to you or your groups."
            : "Raised by you or shared with you."
        }
        actions={
          <Link to="/tickets/new" className="btn btn-primary">
            <Plus size={18} aria-hidden="true" />
            Raise a ticket
          </Link>
        }
      />
      {showAssigned ? (
        <div className="seg whose-switch" role="radiogroup" aria-label="Whose tickets">
          {(
            [
              ["mine", "Raised by you"],
              ["assigned", "Assigned to you"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={whose === value}
              className="seg-item"
              onClick={() => {
                setWhose(value);
                setOffset(0);
              }}
            >
              {label}
              {value === "assigned" && assignedOpen.data?.total ? (
                <span className="seg-count">{assignedOpen.data.total}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
      <div className="toolbar">
        <div className="toolbar-filters">
          <div className="tab-bar" role="radiogroup" aria-label="Show">
            {(
              [
                ["open", "Open"],
                ["done", "Resolved & closed"],
                ["all", "All"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={view === value}
                className="tab-bar-item"
                onClick={() => {
                  setView(value);
                  setOffset(0);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="search">
            <Search className="search-icon" size={18} aria-hidden="true" />
            <input
              className="input"
              type="search"
              aria-label="Search your tickets"
              placeholder="Search by title or reference"
              value={search}
              maxLength={200}
              onChange={(event) => {
                setSearch(event.target.value);
                setOffset(0);
              }}
            />
          </div>
        </div>
        <p className="toolbar-meta" aria-live="polite">
          {tickets.isFetching && !tickets.isPending ? <Spinner /> : null}
          {tickets.data ? <b>{total === 1 ? "1 ticket" : `${total} tickets`}</b> : null}
        </p>
      </div>
      <section className="card table-card" aria-label="Your tickets">
        {tickets.isPending ? (
          <div className="empty-state">
            <Spinner label="Loading tickets" />
          </div>
        ) : tickets.isError ? (
          <ErrorState error={tickets.error} onRetry={() => void tickets.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState icon={<Inbox size={20} />} title={q ? "No tickets match." : view === "open" ? "Nothing open." : "No tickets yet."}>
            <Link to="/tickets/new" className="link-btn">
              Raise a ticket
            </Link>
          </EmptyState>
        ) : (
          <>
            <TicketTable tickets={items} />
            {total > PAGE_SIZE ? (
              <div className="pagination">
                <span>
                  {offset + 1}–{offset + items.length} of {total}
                </span>
                <span className="row">
                  <button className="btn btn-secondary btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                    Previous
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                    Next
                  </button>
                </span>
              </div>
            ) : null}
          </>
        )}
      </section>
    </>
  );
}
